import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, MapPin, MessageCircle, Star, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import ServiceIcon from "../components/ServiceIcon";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const HomePage = () => {
  const { tr } = useLang();
  const [trending, setTrending] = useState([]);
  const [highlights, setHighlights] = useState([]);
  useEffect(() => {
    api.get("/services/public", { params: { trending: true } })
      .then((r) => setTrending((r.data || []).slice(0, 8)))
      .catch(() => {});
    api.get("/store/products", { params: { highlight: true } })
      .then((r) => setHighlights((r.data || []).slice(0, 8)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-medium tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
              {tr(t.hero.kicker)}
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight text-slate-900 leading-[1.05]">
              {tr(t.hero.title)}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              {tr(t.hero.subtitle)}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/submit">
                <Button
                  size="lg"
                  className="bg-orange-600 hover:bg-orange-700 text-white h-12 px-6 rounded-md focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  data-testid="hero-apply-btn"
                >
                  {tr(t.hero.ctaPrimary)} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/track">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6 rounded-md border-slate-300 text-slate-800 hover:bg-slate-50"
                  data-testid="hero-track-btn"
                >
                  {tr(t.hero.ctaSecondary)}
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <Feature icon={<ShieldCheck className="w-5 h-5 text-teal-700" />} title={tr({ en: "Secure", hi: "सुरक्षित" })} desc={tr({ en: "Private storage", hi: "निजी स्टोरेज" })} />
              <Feature icon={<Clock       className="w-5 h-5 text-teal-700" />} title={tr({ en: "Fast",   hi: "तेज़" })}   desc={tr({ en: "Quick turnaround", hi: "जल्दी काम" })} />
              <Feature icon={<MapPin      className="w-5 h-5 text-teal-700" />} title={tr({ en: "Local",  hi: "स्थानीय" })} desc={tr({ en: "Two Bihar branches", hi: "दो बिहार शाखाएँ" })} />
            </div>
          </div>

          <div className="lg:col-span-5 fade-up">
            <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-sm">
              <img
                src="/counter.jpg"
                alt="Ashish Point counter — Dighwa Dubauli branch"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-white/95 backdrop-blur border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-widest text-teal-700 font-medium">{tr({ en: "Trusted by", hi: "भरोसा" })}</div>
                <div className="mt-1 font-heading text-lg text-slate-900">1000+ {tr({ en: "families in Bihar", hi: "बिहार परिवार" })}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Services placeholder (Super Admin will fill this) */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">
              {tr({ en: "Trending Now", hi: "अभी लोकप्रिय" })}
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold text-slate-900">
              {tr({ en: "Trending services this week", hi: "इस सप्ताह की लोकप्रिय सेवाएँ" })}
            </h2>
          </div>
          <Link to="/services" className="text-sm text-teal-700 hover:underline shrink-0" data-testid="home-view-all-services">
            {tr({ en: "View all →", hi: "सभी देखें →" })}
          </Link>
        </div>

        {trending.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white/60 p-10 sm:p-16 text-center">
            <div className="w-12 h-12 mx-auto rounded-md bg-teal-50 text-teal-700 flex items-center justify-center mb-4">
              <Star className="w-6 h-6" />
            </div>
            <div className="text-lg font-heading font-medium text-slate-700">
              {tr({ en: "Browse all our services", hi: "हमारी सभी सेवाएँ देखें" })}
            </div>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              {tr({ en: "Super Admin can mark services as trending — they will appear here.", hi: "सुपर एडमिन सेवाओं को ट्रेंडिंग मार्क कर सकते हैं — वे यहाँ दिखेंगी।" })}
            </p>
            <Link to="/services" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-teal-700 hover:underline">
              {tr({ en: "Browse all services", hi: "सभी सेवाएँ देखें" })} →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {trending.map((s) => (
              <Link key={s.id} to={`/submit?service=${s.key}`} className="group flex flex-col rounded-lg border border-slate-200 bg-white overflow-hidden hover:-translate-y-1 hover:shadow-md transition-transform" data-testid={`trending-card-${s.key}`}>
                <div className="h-32 bg-slate-100">
                  {s.photo_file ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/services/${s.key}/photo`} alt={s.name_en} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-orange-400"><ServiceIcon name={s.icon || "FileText"} className="w-8 h-8" /></div>
                  )}
                </div>
                <div className="flex-1 p-5">
                  <h3 className="font-heading font-semibold text-slate-900 text-base">{tr({ en: s.name_en, hi: s.name_hi })}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{tr({ en: s.desc_en, hi: s.desc_hi })}</p>
                  {s.fee && <div className="mt-3 text-sm font-medium text-teal-700">{s.fee}</div>}
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-orange-700">{tr(t.common.apply)} <ArrowRight className="w-3.5 h-3.5" /></div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {highlights.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-pink-600 font-semibold">AP Digital Store</div>
                <h2 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">{tr({ en: "Highlight Products", hi: "मुख्य प्रोडक्ट" })}</h2>
              </div>
              <Link to="/store" className="text-sm font-medium text-pink-600 hover:underline">{tr({ en: "View all →", hi: "सभी देखें →" })}</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {highlights.map((p) => (
                <Link key={p.id} to={`/store/${p.key}`} className="group flex flex-col rounded-lg border border-slate-200 bg-white overflow-hidden hover:-translate-y-1 hover:shadow-md transition-transform" data-testid={`highlight-card-${p.key}`}>
                  <div className="h-32 bg-slate-100">
                    {p.thumbnail_file ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/store/products/${p.key}/thumbnail`} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-pink-400">⭐</div>
                    )}
                  </div>
                  <div className="flex-1 p-4">
                    <div className="text-xs text-slate-500">{p.category}</div>
                    <h3 className="mt-1 font-heading font-semibold text-slate-900 text-sm line-clamp-2">{p.name}</h3>
                    <p className="mt-1 text-xs text-slate-600 line-clamp-2">{p.short_desc}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-pink-600">{tr({ en: "View Details", hi: "विवरण देखें" })} <ArrowRight className="w-3.5 h-3.5" /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Testimonials */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">
              {tr({ en: "Word of mouth", hi: "लोगों की राय" })}
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold text-slate-900">
              {tr({ en: "Kind words from our neighbours", hi: "हमारे पड़ोसियों के अच्छे शब्द" })}
            </h2>
            <p className="mt-3 text-slate-600">
              {tr({
                en: "Real feedback from families we've helped across Miratola and Dighwa Dubauli.",
                hi: "मिरतोला और डिघवा दुबौली के परिवारों से मिली सच्ची राय।",
              })}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((tm, i) => (
              <figure
                key={i}
                className="relative rounded-xl border border-slate-200 bg-slate-50/50 p-6 hover:-translate-y-1 hover:shadow-md transition-transform"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="absolute top-5 right-5 w-6 h-6 text-teal-700/20" aria-hidden />
                <div className="flex items-center gap-0.5 mb-3" aria-label="5 star rating">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <blockquote className="text-slate-800 leading-relaxed text-[15px]">
                  “{tr({ en: tm.en, hi: tm.hi })}”
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 pt-4 border-t border-slate-200">
                  <div
                    className="w-10 h-10 rounded-full bg-teal-700 text-white flex items-center justify-center font-heading font-semibold text-sm shrink-0"
                    aria-hidden
                  >
                    {tm.initials}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">
                      {tr({ en: tm.nameEn, hi: tm.nameHi })}
                    </div>
                    <div className="text-xs text-slate-500">
                      {tr({ en: tm.metaEn, hi: tm.metaHi })}
                    </div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900">
              {tr({ en: "Not sure which form you need?", hi: "पता नहीं कौन-सा फॉर्म चाहिए?" })}
            </h2>
            <p className="mt-3 text-slate-600 max-w-lg">
              {tr({ en: "Send us a WhatsApp message — Ranjit or Anurag will guide you personally.", hi: "हमें व्हाट्सएप पर संदेश भेजें — रंजित या अनुराग आपको स्वयं मार्गदर्शन देंगे।" })}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a
              href={`https://wa.me/917070335050?text=${encodeURIComponent("Namaste Ashish Point, I need help.")}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-12 px-5 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium"
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> {tr(t.common.whatsapp)}
            </a>
            <Link to="/submit">
              <Button size="lg" className="h-12 px-6 bg-orange-600 hover:bg-orange-700 text-white" data-testid="cta-submit-btn">
                {tr(t.hero.ctaPrimary)}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const Feature = ({ icon, title, desc }) => (
  <div>
    <div className="w-9 h-9 rounded-md bg-teal-50 flex items-center justify-center mb-2">{icon}</div>
    <div className="text-sm font-semibold text-slate-900">{title}</div>
    <div className="text-xs text-slate-500">{desc}</div>
  </div>
);

const TESTIMONIALS = [
  {
    initials: "SK",
    nameEn: "Sunita Kumari",
    nameHi: "सुनीता कुमारी",
    metaEn: "Aadhaar update · Dighwa Dubauli",
    metaHi: "आधार अपडेट · डिघवा दुबौली",
    en: "Ranjit ji fixed my address on Aadhaar in one visit. No running around to the block office — my new card came home in a week.",
    hi: "रंजित जी ने एक ही मुलाकात में मेरा आधार का पता ठीक कर दिया। ब्लॉक ऑफिस के चक्कर नहीं लगाने पड़े — नया कार्ड एक हफ्ते में घर आ गया।",
  },
  {
    initials: "RM",
    nameEn: "Ramesh Mahto",
    nameHi: "रमेश महतो",
    metaEn: "PAN & EPF · Miratola",
    metaHi: "पैन और EPF · मिरतोला",
    en: "I got my PAN and pulled out my EPF from a company I left 5 years ago. Anurag bhai handled everything on WhatsApp — very honest work.",
    hi: "मेरा पैन बना और 5 साल पहले छोड़ी कंपनी का EPF भी निकल गया। अनुराग भाई ने पूरा काम व्हाट्सएप पर संभाला — बहुत ईमानदारी से।",
  },
  {
    initials: "PD",
    nameEn: "Priya Devi",
    nameHi: "प्रिया देवी",
    metaEn: "Scholarship filing · Gopalganj",
    metaHi: "स्कॉलरशिप फाइलिंग · गोपालगंज",
    en: "My daughter's e-Kalyan scholarship was filed correctly the first time. The tracking ID let me check the status from home — really helpful.",
    hi: "बेटी की ई-कल्याण स्कॉलरशिप पहली ही बार में सही से भर गयी। ट्रैकिंग आईडी से घर बैठे स्थिति दिख गयी — बहुत मदद मिली।",
  },
];

export default HomePage;
